/**
 * Phase 3.1: Breakpoint Property Service
 *
 * Handles applying property changes to specific breakpoints in the responsive position system.
 * This service bridges AI chat commands with the responsive design system.
 */

import type { Component, BreakpointOverride, ResponsivePosition } from '@/types';
import type { BreakpointId, ResponsiveStyleChanges } from '@/types/ai';
import { PropertyChange } from './commands/BaseCommandHandler';

// ============================================================================
// Types
// ============================================================================

export interface BreakpointApplicationResult {
  success: boolean;
  /** The updated component data to be applied */
  componentUpdate?: Partial<Component>;
  /** Description of what was applied */
  description: string;
  /** Which breakpoints were affected */
  affectedBreakpoints: BreakpointId[];
  /** Any warnings during application */
  warnings: string[];
}

// ============================================================================
// Service Class
// ============================================================================

class BreakpointPropertyService {
  /**
   * Check if a property change targets a specific breakpoint
   */
  isBreakpointSpecific(changes: PropertyChange): boolean {
    return !!(changes.breakpoint || changes.responsiveStyles);
  }

  /**
   * Apply property changes to a specific breakpoint in the component's responsivePosition
   */
  applyToBreakpoint(
    component: Component,
    changes: PropertyChange,
    breakpoint: BreakpointId
  ): BreakpointApplicationResult {
    console.log(`[Phase 3.1] applyToBreakpoint: breakpoint=${breakpoint}, changes=`, changes);

    if (breakpoint === 'all') {
      // Apply to all breakpoints
      return this.applyToAllBreakpoints(component, changes);
    }

    const warnings: string[] = [];
    const affectedBreakpoints: BreakpointId[] = [breakpoint];

    // Build the breakpoint override from changes
    const override: BreakpointOverride = this.buildBreakpointOverride(changes);

    if (Object.keys(override).length === 0) {
      console.log('[Phase 3.1] No override properties to apply');
      return {
        success: false,
        description: 'No applicable changes for breakpoint',
        affectedBreakpoints: [],
        warnings: ['No style, property, or size changes found in command'],
      };
    }

    // Merge with existing breakpoint override
    const existingBreakpoints = component.responsivePosition?.breakpoints || {};
    const existingOverride = existingBreakpoints[breakpoint as keyof typeof existingBreakpoints] || {};

    const mergedOverride: BreakpointOverride = {
      ...existingOverride,
      ...override,
      // Deep merge style and properties
      style: {
        ...existingOverride.style,
        ...override.style,
      },
      properties: {
        ...existingOverride.properties,
        ...override.properties,
      },
    };

    // Clean up empty objects
    if (Object.keys(mergedOverride.style || {}).length === 0) {
      delete mergedOverride.style;
    }
    if (Object.keys(mergedOverride.properties || {}).length === 0) {
      delete mergedOverride.properties;
    }

    // Build the component update
    const responsivePosition: ResponsivePosition = {
      ...component.responsivePosition,
      breakpoints: {
        ...existingBreakpoints,
        [breakpoint]: mergedOverride,
      },
    };

    const componentUpdate: Partial<Component> = {
      responsivePosition,
    };

    const description = this.generateDescription(changes, breakpoint);

    console.log(`[Phase 3.1] Breakpoint override applied: breakpoint=${breakpoint}`, mergedOverride);

    return {
      success: true,
      componentUpdate,
      description,
      affectedBreakpoints,
      warnings,
    };
  }

  /**
   * Apply responsive styles to multiple breakpoints
   */
  applyResponsiveStyles(
    component: Component,
    responsiveStyles: ResponsiveStyleChanges
  ): BreakpointApplicationResult {
    console.log('[Phase 3.1] applyResponsiveStyles:', responsiveStyles);

    const warnings: string[] = [];
    const affectedBreakpoints: BreakpointId[] = [];

    const existingBreakpoints = component.responsivePosition?.breakpoints || {};
    const newBreakpoints: Record<string, BreakpointOverride> = { ...existingBreakpoints };

    // Process each breakpoint
    for (const [bp, styles] of Object.entries(responsiveStyles)) {
      if (styles && Object.keys(styles).length > 0) {
        const breakpoint = bp as 'mobile' | 'tablet' | 'desktop';
        const existing = existingBreakpoints[breakpoint] || {};

        // Separate style and size properties
        const sizeProps: Record<string, unknown> = {};
        const styleProps: Record<string, unknown> = {};
        const propertyProps: Record<string, unknown> = {};

        for (const [key, value] of Object.entries(styles)) {
          if (key === 'width' || key === 'height') {
            sizeProps[key] = value;
          } else if (key === 'hidden' || key === 'display') {
            if (key === 'display' && value === 'none') {
              // Convert display: none to hidden: true
              newBreakpoints[breakpoint] = {
                ...existing,
                ...newBreakpoints[breakpoint],
                hidden: true,
              };
            } else if (key === 'display' && value !== 'none') {
              newBreakpoints[breakpoint] = {
                ...existing,
                ...newBreakpoints[breakpoint],
                hidden: false,
              };
            } else if (key === 'hidden') {
              newBreakpoints[breakpoint] = {
                ...existing,
                ...newBreakpoints[breakpoint],
                hidden: value as boolean,
              };
            }
          } else if (
            key === 'flexDirection' ||
            key === 'justifyContent' ||
            key === 'alignItems'
          ) {
            propertyProps[key] = value;
          } else {
            styleProps[key] = value;
          }
        }

        // Phase 5 Fix: When padding shorthand is set, clear individual padding properties
        // to prevent CSS specificity conflicts (individual props override shorthand)
        let mergedStyle = { ...existing.style };

        // CRITICAL FIX: When height/width are set in size, clear stale values from style
        // FlexContainer checks style.height first, so stale values would override new size values
        let styleModified = false;
        if ('height' in sizeProps && mergedStyle.height !== undefined) {
          delete mergedStyle.height;
          styleModified = true;
          console.log(`[Phase 3.1] Clearing stale style.height to use new size.height: ${sizeProps.height}`);
        }
        if ('width' in sizeProps && mergedStyle.width !== undefined) {
          delete mergedStyle.width;
          styleModified = true;
          console.log(`[Phase 3.1] Clearing stale style.width to use new size.width: ${sizeProps.width}`);
        }

        if (Object.keys(styleProps).length > 0) {
          // If setting padding shorthand, remove individual padding properties
          if ('padding' in styleProps) {
            delete mergedStyle.paddingTop;
            delete mergedStyle.paddingRight;
            delete mergedStyle.paddingBottom;
            delete mergedStyle.paddingLeft;
          }
          // If setting margin shorthand, remove individual margin properties
          if ('margin' in styleProps) {
            delete mergedStyle.marginTop;
            delete mergedStyle.marginRight;
            delete mergedStyle.marginBottom;
            delete mergedStyle.marginLeft;
          }
          // Merge in new style props
          mergedStyle = { ...mergedStyle, ...styleProps };
        }

        newBreakpoints[breakpoint] = {
          ...existing,
          ...newBreakpoints[breakpoint],
          ...(Object.keys(sizeProps).length > 0 && {
            size: {
              ...existing.size,
              ...sizeProps,
            },
          }),
          // Save style if new styleProps exist OR if we cleared stale height/width
          ...((Object.keys(styleProps).length > 0 || styleModified) && {
            style: mergedStyle,
          }),
          ...(Object.keys(propertyProps).length > 0 && {
            properties: {
              ...existing.properties,
              ...propertyProps,
            },
          }),
        };

        affectedBreakpoints.push(breakpoint);
        console.log(`[Phase 3.1] Breakpoint ${breakpoint} updated:`, newBreakpoints[breakpoint]);
      }
    }

    if (affectedBreakpoints.length === 0) {
      return {
        success: false,
        description: 'No breakpoints to update',
        affectedBreakpoints: [],
        warnings: ['No valid breakpoint styles provided'],
      };
    }

    const responsivePosition: ResponsivePosition = {
      ...component.responsivePosition,
      breakpoints: newBreakpoints,
    };

    const componentUpdate: Partial<Component> = {
      responsivePosition,
    };

    const description = `Applied styles to ${affectedBreakpoints.join(', ')}`;

    return {
      success: true,
      componentUpdate,
      description,
      affectedBreakpoints,
      warnings,
    };
  }

  /**
   * Apply changes to all breakpoints
   */
  private applyToAllBreakpoints(
    component: Component,
    changes: PropertyChange
  ): BreakpointApplicationResult {
    console.log('[Phase 3.1] applyToAllBreakpoints:', changes);

    const override = this.buildBreakpointOverride(changes);
    const existingBreakpoints = component.responsivePosition?.breakpoints || {};

    const newBreakpoints: Record<string, BreakpointOverride> = {};
    const breakpointNames: Array<'mobile' | 'tablet' | 'desktop'> = ['mobile', 'tablet', 'desktop'];

    for (const bp of breakpointNames) {
      const existing = existingBreakpoints[bp] || {};
      newBreakpoints[bp] = {
        ...existing,
        ...override,
        style: {
          ...existing.style,
          ...override.style,
        },
        properties: {
          ...existing.properties,
          ...override.properties,
        },
      };

      // Clean up empty objects
      if (Object.keys(newBreakpoints[bp].style || {}).length === 0) {
        delete newBreakpoints[bp].style;
      }
      if (Object.keys(newBreakpoints[bp].properties || {}).length === 0) {
        delete newBreakpoints[bp].properties;
      }
    }

    const responsivePosition: ResponsivePosition = {
      ...component.responsivePosition,
      breakpoints: newBreakpoints,
    };

    const componentUpdate: Partial<Component> = {
      responsivePosition,
    };

    return {
      success: true,
      componentUpdate,
      description: 'Applied changes to all breakpoints',
      affectedBreakpoints: ['mobile', 'tablet', 'desktop'],
      warnings: [],
    };
  }

  /**
   * Build a BreakpointOverride from PropertyChange
   *
   * IMPORTANT: For flex containers and cards, dimensions must be stored in
   * style.width/height, NOT in size. The ComponentWrapper uses breakpoint
   * style for dimensions, not the size property.
   */
  private buildBreakpointOverride(changes: PropertyChange): BreakpointOverride {
    const override: BreakpointOverride = {};

    if (changes.style && Object.keys(changes.style).length > 0) {
      // Handle display: none specially
      if (changes.style.display === 'none') {
        override.hidden = true;
      } else if (changes.style.display === 'block' || changes.style.display === 'flex') {
        override.hidden = false;
      }

      // Copy other style properties
      const styleWithoutDisplay = { ...changes.style };
      if (styleWithoutDisplay.display === 'none' || styleWithoutDisplay.display === 'block') {
        delete styleWithoutDisplay.display;
      }

      if (Object.keys(styleWithoutDisplay).length > 0) {
        override.style = styleWithoutDisplay;
      }
    }

    if (changes.properties && Object.keys(changes.properties).length > 0) {
      // Handle hidden property specially
      if ('hidden' in changes.properties) {
        override.hidden = changes.properties.hidden === true;
        const propsWithoutHidden = { ...changes.properties };
        delete propsWithoutHidden.hidden;

        if (Object.keys(propsWithoutHidden).length > 0) {
          override.properties = propsWithoutHidden;
        }
      } else {
        override.properties = changes.properties;
      }
    }

    // Phase 5.4 FIX: Put size changes into BOTH size AND style.width/height
    // ComponentWrapper reads dimensions from style, so we must include them there
    if (changes.size) {
      override.size = changes.size;

      // Also add to style for ComponentWrapper compatibility
      if (!override.style) {
        override.style = {};
      }
      if (changes.size.width !== undefined) {
        override.style.width = changes.size.width;
        console.log('[Phase 5.4] BreakpointPropertyService: Added width to style:', changes.size.width);
      }
      if (changes.size.height !== undefined) {
        override.style.height = changes.size.height;
        console.log('[Phase 5.4] BreakpointPropertyService: Added height to style:', changes.size.height);
      }
    }

    if (changes.position) {
      override.position = changes.position;
    }

    return override;
  }

  /**
   * Generate a user-friendly description of the change
   */
  private generateDescription(changes: PropertyChange, breakpoint: BreakpointId): string {
    const parts: string[] = [];
    const bp = breakpoint.charAt(0).toUpperCase() + breakpoint.slice(1);

    if (changes.style?.display === 'none' || changes.properties?.hidden === true) {
      parts.push(`Hidden on ${bp}`);
    } else if (changes.style?.display === 'block' || changes.properties?.hidden === false) {
      parts.push(`Shown on ${bp}`);
    }

    if (changes.size?.width) {
      parts.push(`Width set to ${changes.size.width} on ${bp}`);
    }

    if (changes.size?.height) {
      parts.push(`Height set to ${changes.size.height} on ${bp}`);
    }

    if (changes.style) {
      const styleKeys = Object.keys(changes.style).filter(k => k !== 'display');
      if (styleKeys.length > 0) {
        parts.push(`Style updated on ${bp}`);
      }
    }

    if (changes.properties) {
      const propKeys = Object.keys(changes.properties).filter(k => k !== 'hidden');
      if (propKeys.length > 0) {
        parts.push(`Properties updated on ${bp}`);
      }
    }

    return parts.length > 0 ? parts.join(', ') : `Changes applied to ${bp}`;
  }

  /**
   * Get example breakpoint commands for suggestions
   */
  getBreakpointExamples(): string[] {
    return [
      'hide on mobile',
      'show on desktop',
      'full width on mobile',
      'w 200 on tablet',
      'red on desktop',
      'stack on mobile, horizontal on desktop',
      'padding 32 on desktop only',
      'smaller text on mobile',
    ];
  }
}

// Export singleton instance
export const breakpointPropertyService = new BreakpointPropertyService();
